import { CloseCircleFilled, ExclamationCircle } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import IconButton from '@ixigo/iui/IconButton';
import Input from '@ixigo/iui/Input';
import Radio from '@ixigo/iui/Radio';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { ChangeEvent, useEffect, useState } from 'react';
import placeholder from '../../../images/filePlaceholder.svg';
import BottomsheetHeader from '../../BottomsheetHeader';
import Dialog from '../../Dialog';
import Toast from '../../Toast';
import { isPassengerNameModified } from './helper';
import { PassengerNameFormData, PassengerNameFormProps } from './index.types';

const SALUTATION_OPTIONS = [
    { label: 'Mr', value: 'Mr' },
    { label: 'Ms', value: 'Ms' },
    { label: 'Mrs', value: 'Mrs' },
];

const uploaderClassName = 'flex gap-10 items-center justify-between px-15 py-10 border border-neutral-100 rounded-10';

function PassengerNameForm({ data, open, onClose, onSubmit }: PassengerNameFormProps) {
    const [formData, setFormData] = useState<PassengerNameFormData>({
        title: '',
        firstName: '',
        lastName: '',
        updatedTitle: '',
        updatedFirstName: '',
        updatedLastName: '',
    });
    const [toast, setToast] = useState(false);

    useEffect(() => {
        if (!data) return;
        setFormData({ ...data });
    }, [data]);

    const disabled = !isPassengerNameModified(formData) || !formData.proofDocument;

    const validateInputFile = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files![0];

        if (selectedFile.size / (1024 * 1024) > 3) {
            setToast(true);
            e.target.value = '';
            return null;
        }

        return selectedFile;
    };

    const selectFile = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = validateInputFile(e);
        if (!selectedFile) return;

        setFormData((prev) => ({ ...prev, proofDocument: selectedFile }));
    };

    const renderUploader = () => {
        const { proofDocument } = formData;

        if (!proofDocument) {
            return (
                <>
                    <div className={uploaderClassName}>
                        <div className='flex flex-col gap-0.5'>
                            <Typography className='font-medium'>Attach ID Proof</Typography>
                            <Typography className='text-neutral-600'>
                                Select a valid PDF or image file. This is mandatory for the airline to process the
                                request.
                            </Typography>
                        </div>
                        <Button size='sm' variant='flat' className='shrink-0'>
                            <label htmlFor='uploadFile'>
                                <input
                                    id='uploadFile'
                                    type='file'
                                    accept='.pdf,.jpg,.jpeg,.png'
                                    placeholder='Upload'
                                    className='hidden'
                                    onChange={selectFile}
                                />
                                Upload
                            </label>
                        </Button>
                    </div>
                    <div className='bg-[#D9EBFF] p-10 rounded-10'>
                        <Typography>
                            Please upload a valid supporting document (Driving License, Passport, or Aadhaar Card). For
                            international itineraries, only passport is accepted. Invalid submissions will result in
                            request rejection from the airline.
                        </Typography>
                    </div>
                </>
            );
        }

        const isImg = proofDocument.type.includes('image');

        return (
            <div className={uploaderClassName}>
                <div className='flex items-center gap-10'>
                    <img
                        src={isImg ? URL.createObjectURL(proofDocument) : placeholder}
                        className={clsx('w-9 h-9 rounded-5 object-cover', isImg && 'border border-neutral-100')}
                        alt={proofDocument.name}
                    />
                    <Typography>{proofDocument.name}</Typography>
                </div>
                <IconButton
                    color='neutral'
                    size='sm'
                    variant='flat'
                    onClick={() => setFormData((prev) => ({ ...prev, proofDocument: undefined }))}
                >
                    <CloseCircleFilled className='text-neutral-600' />
                </IconButton>
            </div>
        );
    };

    return (
        <>
            <Dialog open={open} classes={{ container: '!min-w-full' }} onClose={onClose}>
                <BottomsheetHeader title='Enter Correct Name' onClose={onClose} />
                <div className='flex flex-col gap-15 grow p-20'>
                    <div className='flex gap-40 p-5'>
                        {SALUTATION_OPTIONS.map((item) => (
                            <div
                                key={item.value}
                                className='flex items-center gap-5'
                                onClick={() => setFormData((prev) => ({ ...prev, updatedTitle: item.value }))}
                            >
                                <Radio checked={formData.updatedTitle === item.value} className='m-0.5' />
                                <Typography variant='body-md'>{item.label}</Typography>
                            </div>
                        ))}
                    </div>
                    <Input
                        label='First & Middle Name'
                        value={formData.updatedFirstName}
                        variant='outlined'
                        onChange={(e) => setFormData((prev) => ({ ...prev, updatedFirstName: e.target.value }))}
                    />
                    <Input
                        label='Last Name'
                        value={formData.updatedLastName}
                        variant='outlined'
                        onChange={(e) => setFormData((prev) => ({ ...prev, updatedLastName: e.target.value }))}
                    />
                    {renderUploader()}
                </div>
                <div className='border-t p-4'>
                    <Button size='lg' onClick={() => onSubmit(formData)} className='w-full' disabled={disabled}>
                        Submit
                    </Button>
                </div>
            </Dialog>
            <Toast
                heading='Cannot upload files larger than 3MB'
                onBackdropClick={() => setToast(false)}
                iconLeft={<ExclamationCircle fontSize={20} />}
                visible={toast}
            />
        </>
    );
}

export default PassengerNameForm;
